import React, { useState } from "react";

import { useForm } from "react-hook-form";
import * as yup from "yup";

import { Button, Collapse, Stack } from "@mui/material";

import ExplicitPolicyConsent from "@app/analytics/auth/components/ExplicitPolicyConsent";
import Separator from "@app/analytics/auth/Separator";
import TextField from "@app/analytics/components/TextField";
import useAuth from "@app/common/auth/useAuth";
import type { EmailPassSignUpData, SSOProviderId } from "@app/common/types";
import * as PolicyUtils from "@app/common/utils/policyUtils";
import { yupResolver } from "@app/utils/forms/yupResolver";

import { Row } from "@rui/Stack";

import SignupProviders from "./SignupProviders";
import AuthButton from "../components/AuthButton";
import TermsSection from "../components/TermsSection";

interface EmailContentProps {
  loading: boolean;
  onEmailContinue: (email: string) => void;
  onOAuthContinue: (id: SSOProviderId) => void;
  email?: string;
}

const emailValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Please enter a valid email"),
});

const emailAndPolicyValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email")
    .required("Please enter a valid email"),
  policyAccepted: yup.boolean().oneOf([true], "Please confirm to continue"),
});

const EmailContent = ({
  loading,
  onEmailContinue,
  onOAuthContinue,
}: EmailContentProps) => {
  const auth = useAuth();

  const [showEmailPasswordForm, setShowEmailPasswordForm] = useState(false);

  const schema = PolicyUtils.newPoliciesReleased()
    ? emailAndPolicyValidationSchema
    : emailValidationSchema;
  const emailInputForm = useForm<EmailPassSignUpData>({
    resolver: yupResolver(schema),
    defaultValues: PolicyUtils.newPoliciesReleased()
      ? { policyAccepted: false }
      : undefined,
  });

  const {
    formState: { errors },
    handleSubmit: handleFormSubmit,
    register,
  } = emailInputForm;

  const hasEmailError = !!(errors.email || (auth.errors.signUp || {}).email);
  const emailErrorMessage =
    (!!errors.email && errors.email.message) ||
    (hasEmailError && auth.errors.signUp?.email) ||
    null;

  const handleSubmit = handleFormSubmit(() => {
    const { email } = emailInputForm.getValues();
    emailInputForm.reset();

    onEmailContinue(email);
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        return handleSubmit(e);
      default:
        return;
    }
  };

  const policyAcceptedField = register("policyAccepted");
  const policyAccepted = emailInputForm.watch("policyAccepted");

  const handleClickProviderSignup = async (providerId: SSOProviderId) => {
    if (PolicyUtils.newPoliciesReleased()) {
      // before account creation, and only when the new policies are released,
      // require explicit acceptance of the new policies
      const isValid = await emailInputForm.trigger("policyAccepted");
      if (isValid) {
        onOAuthContinue(providerId);
      }
    } else {
      onOAuthContinue(providerId);
    }
  };

  return (
    <Stack
      component="form"
      sx={{ width: "100%" }}
      direction="column-reverse"
      onSubmit={handleSubmit}
    >
      <Stack sx={{ width: "100%" }}>
        <Collapse in={!showEmailPasswordForm} unmountOnExit>
          <Row variant="center" sx={{ pb: 1 }}>
            <Button
              data-test-id="auth-button-sign-up-with-email"
              color="secondary"
              onClick={() => setShowEmailPasswordForm(true)}
            >
              Sign up with email
            </Button>
          </Row>
        </Collapse>
        <Collapse in={showEmailPasswordForm} mountOnEnter>
          <TextField
            data-test-id="auth-email-input"
            label="Work email address"
            error={hasEmailError}
            helperText={emailErrorMessage}
            InputProps={{
              ...register("email"),
              placeholder: "Enter your work email",
              sx: { height: "46px" },
              onKeyDown: handleKeyDown,
            }}
          />
          <AuthButton
            data-test-id="auth-button-continue-sign-up"
            sx={{ mt: "30px" }}
            type="submit"
            loading={loading}
          >
            Continue
          </AuthButton>
        </Collapse>
        <Row variant="center">
          {PolicyUtils.newPoliciesReleased() ? (
            <ExplicitPolicyConsent
              {...policyAcceptedField}
              consented={policyAccepted}
              errorMessage={errors.policyAccepted?.message}
            />
          ) : (
            <TermsSection context="signup" />
          )}
        </Row>
      </Stack>
      <Separator>or</Separator>
      <Collapse in={!showEmailPasswordForm} unmountOnExit>
        <SignupProviders onClickProvider={handleClickProviderSignup} />
      </Collapse>
      <Collapse in={showEmailPasswordForm} unmountOnExit>
        <Row variant="center">
          <Button
            data-test-id="auth-button-sign-up-with-sso"
            color="secondary"
            onClick={() => setShowEmailPasswordForm(false)}
          >
            Sign up with Single Sign-On (SSO)
          </Button>
        </Row>
      </Collapse>
    </Stack>
  );
};

export default EmailContent;
